import React, { useState, useEffect, useMemo, useRef } from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Card, Box, Button, Snackbar } from "common";
import BulkJobModal from "./BulkJobModal";
import BulkJobItems from "./BulkJobItem";
import { IState } from "store/store.interface";
import { Add } from "@material-ui/icons";
import { useSelector, useDispatch } from "react-redux";
import { setLocation } from "store/actions/auth.actions";
import {
  fetchBulkBundleJobs,
  clearBulkBundleJobList,
} from "store/actions/bulkBundleUtility.actions";
import LoadMoreBulkJobs from "../LoadMoreBulkJobs";
import { useApiInfo } from "hooks";
import {
  BULK_BUNDLE_UPDATE_JOB_STATUS,
  BULK_BUNDLE_FETCH_JOBS,
  CREATE_BULK_BUNDLE_JOB,
  BULK_BUNDLE_RESOURCE_GET_BY_ID,
} from "store/store.types";
import { clearApi } from "store/actions/api.actions";
import EmptyBulkJobs from "../EmptyBulkJobs";
import { useStyles, StyledTableCell } from "./BulkJobs.style";
import LoaderBulkJobs from "Loader/Dashboard";
import { isEmpty } from "utils/app.utils";

const BulkUploadJobs = () => {
  const classes = useStyles();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [bulkJobError, setBulkJobError] = useState<string[]>([]);

  const dispatch = useDispatch();

  const {
    canCreateCampaigns = [],
    roles = [],
    newBulkBundleJob,
    bulkBundleJobs,
    searchTerm = "",
  } = useSelector((state: IState) => ({
    ...state.auth,
    ...state.config,
    ...state.bulkBundlePromotion,
    ...state.promotion,
  }));

  const latestSearchTerm = useRef(searchTerm);
  const { error, isLoading } = useApiInfo(BULK_BUNDLE_FETCH_JOBS);
  const { error: updateStatusError } = useApiInfo(
    BULK_BUNDLE_UPDATE_JOB_STATUS
  );
  const { error: fileDownloadError } = useApiInfo(
    BULK_BUNDLE_RESOURCE_GET_BY_ID
  );

  const onCloseError = () => {
    dispatch(clearApi(BULK_BUNDLE_FETCH_JOBS));
    dispatch(clearApi(BULK_BUNDLE_UPDATE_JOB_STATUS));
    dispatch(clearApi(BULK_BUNDLE_RESOURCE_GET_BY_ID));
  };

  useEffect(() => {
    if (newBulkBundleJob) {
      dispatch(fetchBulkBundleJobs());
    }
  }, [newBulkBundleJob, dispatch]);

  useEffect(() => {
    if (!bulkBundleJobs || bulkBundleJobs?.length === 0) {
      dispatch(fetchBulkBundleJobs());
    }
  }, [dispatch]);

  // This is used to keep the latest search term
  useEffect(() => {
    latestSearchTerm.current = searchTerm;
  }, [searchTerm]);

  // This is used to clear list when unmounting if a search was active
  useEffect(() => {
    return () => {
      if (latestSearchTerm.current) {
        dispatch(clearBulkBundleJobList());
      }
    };
  }, [dispatch]);

  const canCreateCampaign = useMemo(() => {
    const role = roles[0];
    return canCreateCampaigns && canCreateCampaigns.indexOf(role) > -1;
  }, [roles, canCreateCampaigns]);

  const onClickHandler = () => {
    dispatch(setLocation("/create-bulk-campaign"));
    dispatch(clearApi(CREATE_BULK_BUNDLE_JOB));
  };

  const handleViewErrorModal = (errors: string[]) => {
    setOpenModal(true);
    setBulkJobError(errors);
  };

  if (isEmpty(bulkBundleJobs) && isLoading) {
    return <LoaderBulkJobs />;
  }

  return (
    <>
      {(error || updateStatusError || fileDownloadError) && (
        <Snackbar
          horizontal="center"
          vertical="top"
          variant="filled"
          severity="error"
          handleClose={onCloseError}
          open={!!error || !!updateStatusError || !!fileDownloadError}
        >
          {error || updateStatusError || fileDownloadError}
        </Snackbar>
      )}
      <Box
        display="flex"
        justifyContent="flex-end"
        my={["0px", "15px", "15px", "15px"]}
      >
        {canCreateCampaign && (
          <Button
            fontSize="14px"
            fontWeight={600}
            minWidth="220px"
            padding="10px"
            round
            color="secondary"
            onClick={onClickHandler}
            variant="contained"
          >
            <Box
              className={classes.bulkCreateButton}
              display="flex"
              alignItems="center"
            >
              <Add color="inherit" /> <span>Create Bulk Campaign</span>
            </Box>
          </Button>
        )}
      </Box>
      <Card padding="40px">
        <BulkJobModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          errorList={bulkJobError}
        />
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Job Name</StyledTableCell>
              <StyledTableCell align="left">Uploaded By</StyledTableCell>
              <StyledTableCell align="left">Date</StyledTableCell>
              <StyledTableCell align="center">Bundles</StyledTableCell>
              <StyledTableCell align="center">Status</StyledTableCell>
              <StyledTableCell align="right" style={{ minWidth: "170px" }}>
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bulkBundleJobs &&
              bulkBundleJobs?.length > 0 &&
              bulkBundleJobs.map((jobInfo) => (
                <BulkJobItems
                  jobInfo={jobInfo}
                  key={jobInfo.id}
                  canCreateCampaign={canCreateCampaign}
                  handleViewErrorModal={handleViewErrorModal}
                />
              ))}
          </TableBody>
        </Table>
        {(!bulkBundleJobs || bulkBundleJobs.length === 0) && <EmptyBulkJobs />}
        {bulkBundleJobs && bulkBundleJobs?.length > 0 && <LoadMoreBulkJobs />}
      </Card>
    </>
  );
};

export default BulkUploadJobs;
